import QuickComposeAttendeeRows from './QuickComposeAttendeeRows';
import QuickComposeCollabSpaceRow from './QuickComposeCollabSpaceRow';
import QuickComposeLocationWell from './QuickComposeLocationWell';
import RecurrenceComponent from './RecurrenceComponent';
import TimeComponent from './TimeComponent';
import TitleAndSuggestionsWithCharm from './TitleAndSuggestionsWithCharm';
import getComposeStyles from '../utils/getComposeStyles';
import { Icon } from '@fluentui/react/lib/Icon';
import { Link } from '@fluentui/react/lib/Link';
import { observer as observer_0 } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { CalendarChannelPicker } from 'owa-calendar-channel-picker';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getQuickComposeViewState } from 'owa-calendar-compose-store-manager';
import isNewEvent from 'owa-calendar-compose-form-store/lib/selectors/isNewEvent';
import isQuickComposeInEditMode from 'owa-calendar-compose-form-store/lib/selectors/isQuickComposeInEditMode';
import canChangeCalendar from 'owa-calendar-compose-form-store/lib/utils/canChangeCalendar';
import { isEventInstanceOfSeries, isOnGroupCalendar } from 'owa-calendar-event-capabilities';
import { isCssVariablesFeatureEnabled } from 'owa-calendar-feature-flags';
import AllDayToggle from 'owa-calendar-forms-common/lib/components/AllDayToggle';
import getGroupAsAttendee from 'owa-calendar-forms-common/lib/utils/getGroupAsAttendee';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import { ControlIcons } from 'owa-control-icons';
import { EmailReminderEntrySource, ReminderDropdown } from 'owa-email-reminders';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import { invitationWontBeSentToGroup } from 'owa-locstrings/lib/strings/invitationwontbesenttogroup.locstring.json';
import { sendEventToAllGroupMembers } from 'owa-locstrings/lib/strings/sendeventtoallgroupmembers.locstring.json';
import { lazyCanTravelToEvent, MobilityFullComposeRow } from 'owa-mobility-compose';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import type RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import * as React from 'react';
import { QuickComposeControl } from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import {
    onCalendarIdChanged,
    onChannelChanged,
    onRequiredAttendeesChanged,
    logQuickComposeControlUsed,
    onReminderChanged,
    updateAllDay,
    setRecurrence,
} from 'owa-calendar-compose-form-actions';
import { LazyQuickComposeEditor } from '../contentsIndex';
import type { CreateCollabSpaceOnInitProps } from 'owa-calendar-helpers-types';
import type { CalendarId } from 'owa-graph-schema';
import type { ChannelOption } from 'owa-calendar-channel-picker/lib/store/schema/CalendarChannelPickerInterfaces';

import styles from './QuickComposeContent.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

/**
 * Props for quick compose content
 */
export interface QuickComposeContentProps {
    formId: string;
    event: CalendarEvent;
    colorScheme: ColorScheme;
    onSizeUpdate: () => void;
    onLaunchFullComposeCallback?: (
        shouldOpenSchedulingAssistant?: boolean,
        createCollabSpaceOnInitProps?: CreateCollabSpaceOnInitProps
    ) => void;
}

/**
 * Component for rendering quick compose
 */
export default observer_0(function QuickComposeContent(props: QuickComposeContentProps) {
    const { formId, onLaunchFullComposeCallback, event, colorScheme, onSizeUpdate } = props;

    React.useEffect(onSizeUpdate);

    /**
     * Adds the group as an attendee to the event and Opens full compose
     */
    const addGroupAttendeeAndOpenFullCompose = e => {
        const { event } = props;
        logUsage('inviteGroupMembersClicked', ['QuickCompose']);
        let mergedAttendees: AttendeeType[] = [];
        mergedAttendees.push(...event.RequiredAttendees);
        mergedAttendees.push(getGroupAsAttendee(event));

        onRequiredAttendeesChanged(mergedAttendees, formId);

        onLaunchFullComposeCallback();
    };
    /**
     * Returns true if the form is in edit mode and the item being edited
     * is an instance of a series.
     */
    const isInEditSeriesInstanceMode = (): boolean => {
        const { event } = props;
        return isQuickComposeInEditMode(formId) && isEventInstanceOfSeries(event);
    };
    const onRecurrenceChanged = (newRecurrence: RecurrenceType): void => {
        logQuickComposeControlUsed(formId, QuickComposeControl.Recurrence, true);
        setRecurrence(newRecurrence, formId);
    };
    const storeViewState = getQuickComposeViewState(formId);

    const onChannelCalendarIdChanged = React.useCallback(
        (calendarId: CalendarId) => {
            onCalendarIdChanged(calendarId, formId);
        },
        [formId]
    );

    const onChannelCalendarChanged = React.useCallback(
        (channelOption: ChannelOption) => {
            onChannelChanged(channelOption, formId);
        },
        [formId]
    );

    const onTimeSuggestionNotFound = React.useCallback(() => {
        onLaunchFullComposeCallback(true /* shouldOpenSchedulingAssistant */);
    }, [onLaunchFullComposeCallback]);

    const onAllDayToggled = React.useCallback(
        (isAllDay: boolean) => {
            logQuickComposeControlUsed(formId, QuickComposeControl.AllDay, true /* isFocus */);
            updateAllDay(isAllDay, true /* isAllDayEventExplicitlySet */, formId);
        },
        [formId]
    );

    const onReminderChanged_0 = React.useCallback(
        (reminderInMinutes: number) => {
            logQuickComposeControlUsed(formId, QuickComposeControl.Reminder, true /* isFocus */);
            onReminderChanged(reminderInMinutes, formId);
        },
        [formId]
    );

    const isInEditMode = isQuickComposeInEditMode(formId);
    const { ReminderMinutesBeforeStart, ReminderIsSet } = event;
    const selectedReminderTimeInMinutes = ReminderIsSet ? ReminderMinutesBeforeStart : -1;
    const isBasicCompose = !isInEditMode;
    const calendarTheme = themeFromColorScheme(colorScheme);
    // This function is to support cal-css-variables feature flag and generate the expected style
    const composeGeneratedStyles = getComposeStyles(
        !isCssVariablesFeatureEnabled(),
        colorScheme,
        styles.useCssVariables,
        styles.editTitleTextInputCssVariables
    );
    // Only show recipient well for new events by enterprise users
    const showRecipientWell: boolean = !isConsumer() && !isInEditMode;
    const showRecipientWellAvailability: boolean = showRecipientWell && !event.Recurrence;
    const isTimePanelViewType = storeViewState.viewType == 'TimePanel';
    const calendarPickerCalloutStyle = isTimePanelViewType
        ? styles.calendarChannelPickerCalloutTimePanel
        : isInEditMode
        ? styles.calendarChannelPickerCalloutEditPeek
        : null;
    let showMobilityCompose = false;
    if (isFeatureEnabled('cal-mf-mobility')) {
        const canTravelToEvent = lazyCanTravelToEvent.tryImportForRender();

        if (canTravelToEvent) {
            showMobilityCompose = canTravelToEvent(event);
        }
    }
    return (
        <div
            className={classNames(
                isBasicCompose ? styles.basicQuickComposeContainer : styles.composeContainer,
                {
                    isCalendarPickerAboveTitle: isTimePanelViewType,
                }
            )}>
            <div className={classNames(styles.composeRow, styles.calendarChannelPickerContainer)}>
                <CalendarChannelPicker
                    event={event}
                    colorScheme={colorScheme}
                    readonly={!canChangeCalendar(formId)}
                    styles={{
                        textField: styles.calendarChannelPickerTextField,
                        textFieldRoot: !isTimePanelViewType
                            ? isInEditMode
                                ? styles.calendarPickerTextFieldRootEditMode
                                : styles.calendarPickerTextFieldRoot
                            : styles.calendarPickerTextFieldRootTimePanel,
                        callout: calendarPickerCalloutStyle,
                        optionsContainer: !isTimePanelViewType
                            ? styles.calendarPickerOptionsContainerPeek
                            : null,
                    }}
                    onCalendarIdChanged={onChannelCalendarIdChanged}
                    onChannelChanged={onChannelCalendarChanged}
                    formId={formId}
                />
            </div>
            <div
                className={classNames(
                    isInEditMode ? styles.editTitleContainer : styles.titleContainer,
                    { smallBottomMargin: showRecipientWell }
                )}>
                <TitleAndSuggestionsWithCharm
                    formId={formId}
                    colorScheme={colorScheme}
                    isInEditMode={isInEditMode}
                />
                <AllDayToggle
                    isAllDay={event.IsAllDayEvent}
                    onAllDayToggled={onAllDayToggled}
                    colorScheme={colorScheme}
                />
            </div>
            {showRecipientWell && (
                <QuickComposeAttendeeRows
                    formId={formId}
                    colorScheme={colorScheme}
                    theme={calendarTheme}
                    isTimePanelViewType={isTimePanelViewType}
                    showAvailability={showRecipientWellAvailability}
                />
            )}
            <TimeComponent
                formId={formId}
                isInEditMode={isInEditMode}
                colorScheme={colorScheme}
                event={event}
                isTimePanelViewType={isTimePanelViewType}
                onSizeUpdate={onSizeUpdate}
                onTimeSuggestionNotFound={onTimeSuggestionNotFound}
            />
            <div className={classNames(styles.composeRow, styles.locationComposeRow)}>
                <Icon iconName={ControlIcons.POI} className={classNames(styles.locationRowIcon)} />
                <QuickComposeLocationWell formId={formId} isNarrow={isInEditMode} />
            </div>
            {showMobilityCompose && (
                <div className={classNames(styles.composeRow, styles.mobilityComposeRow)}>
                    <MobilityFullComposeRow event={event} entrySource={'QuickCompose_Edit'} />
                </div>
            )}
            {!isInEditSeriesInstanceMode() && (
                <div className={classNames(styles.composeRow, styles.recurrenceContainer)}>
                    <Icon
                        iconName={ControlIcons.SyncOccurence}
                        className={styles.recurrenceRowIcon}
                    />
                    <RecurrenceComponent
                        colorScheme={colorScheme}
                        className={styles.recurrenceComponent}
                        event={event}
                        onRecurrenceChangedCallback={onRecurrenceChanged}
                        cssVariablesStyle={composeGeneratedStyles}
                        onSizeUpdate={onSizeUpdate}
                        isNewEvent={isNewEvent(formId)}
                    />
                </div>
            )}
            <div className={classNames(styles.composeRow, styles.reminderContainer)}>
                <Icon iconName={ControlIcons.AlarmClock} className={styles.reminderRowIcon} />
                <ReminderDropdown
                    event={event}
                    colorScheme={colorScheme}
                    entrySource={EmailReminderEntrySource.QuickCompose}
                    onReminderChanged={onReminderChanged_0}
                    selectedReminderTimeInMinutes={selectedReminderTimeInMinutes}
                    viewStateId={formId}
                />
            </div>
            {!isInEditMode && <LazyQuickComposeEditor formId={formId} colorScheme={colorScheme} />}
            <QuickComposeCollabSpaceRow
                event={event}
                composeStyles={composeGeneratedStyles}
                launchFullCompose={onLaunchFullComposeCallback}
            />
            {isOnGroupCalendar(event) && (
                <div className={classNames(styles.composeRow, styles.infoBarRowContainer)}>
                    <Icon iconName={ControlIcons.Info} className={styles.infoBarRowIcon} />
                    <div className={styles.infoBarMessageContainer}>
                        {loc(invitationWontBeSentToGroup)}
                        <Link
                            styles={composeGeneratedStyles.linkGetStyles}
                            onClick={addGroupAttendeeAndOpenFullCompose}
                            className={classNames(
                                styles.infoBarMessageLine,
                                composeGeneratedStyles.cssVariabelsStyle
                            )}>
                            {loc(sendEventToAllGroupMembers)}
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
});
